import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  ShareIcon,
  BookmarkIcon,
  ChevronLeftIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Sample event data - in real app, this would be fetched based on ID
  const [event] = useState({
    id: parseInt(id) || 1,
    title: "Independence Day Celebration",
    date: "2025-08-15",
    time: "09:00 AM - 05:00 PM",
    location: "Apollo Main Campus, Noida",
    category: "National Holiday",
    attendees: 500,
    maxAttendees: 600,
    description: "Join us for a special Independence Day celebration with cultural programs, team activities, and a flag hoisting ceremony. This year's celebration will feature performances by various departments, patriotic music, traditional food stalls, and team building activities.",
    status: "upcoming",
    color: "red",
    organizer: {
      name: "HR Department",
      contact: "+91-9876543210",
      email: "hr@apollo.com"
    },
    agenda: [
      { time: "09:00 AM", event: "Registration & Welcome Coffee" },
      { time: "09:30 AM", event: "Flag Hoisting Ceremony" },
      { time: "10:00 AM", event: "Cultural Performances" },
      { time: "12:00 PM", event: "Traditional Lunch" },
      { time: "02:00 PM", event: "Team Building Activities" },
      { time: "04:00 PM", event: "Prize Distribution" },
      { time: "05:00 PM", event: "Closing Ceremony" }
    ],
    attendeesList: [
      { id: 1, name: "Dr. Priya Sharma", department: "Cardiology", avatar: "PS" },
      { id: 2, name: "Dr. Rajesh Kumar", department: "Neurology", avatar: "RK" },
      { id: 3, name: "Deepika Joshi", department: "Laboratory", avatar: "DJ" },
      { id: 4, name: "Arjun Malhotra", department: "IT Support", avatar: "AM" },
      { id: 5, name: "Sushma Iyer", department: "Surgery", avatar: "SI" }
    ],
    requirements: [
      "Please bring your ID card for entry",
      "Comfortable clothing recommended for activities",
      "Lunch will be provided for all registered attendees",
      "Photography allowed during cultural programs"
    ],
    images: [
      "https://images.unsplash.com/photo-1586297135537-94bc9ba060aa?w=400&h=300&fit=crop",
      "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop"
    ]
  });

  const [isRegistered, setIsRegistered] = useState(false);

  const handleRegistration = () => {
    setIsRegistered(!isRegistered);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      red: 'bg-red-50 border-red-200 text-red-800',
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
    };
    return colorMap[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Component */}
      <Header />

      {/* Event Detail Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-start space-x-3 sm:space-x-4">
              <button 
                onClick={() => navigate('/events')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">{event.title}</h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Event Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 self-start sm:self-auto">
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <BookmarkIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
              <button 
                onClick={handleRegistration}
                className={`px-4 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                  isRegistered 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-apollo-accent text-white hover:bg-apollo-primary'
                }`}
              >
                {isRegistered ? 'Registered ✓' : 'Register Now'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Event Images */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2">
                {event.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Event ${index + 1}`}
                    className="w-full h-32 sm:h-40 md:h-48 object-cover hover:opacity-90 transition-opacity cursor-pointer"
                  />
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">About This Event</h2>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{event.description}</p>
            </div>

            {/* Agenda */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Event Agenda</h2>
              <div className="space-y-3 sm:space-y-4">
                {event.agenda.map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 p-3 border-l-4 border-apollo-accent bg-gray-50 rounded-r-lg">
                    <div className="text-apollo-accent font-semibold text-sm sm:text-base min-w-0 sm:min-w-[80px]">
                      {item.time}
                    </div>
                    <div className="text-gray-900 text-sm sm:text-base">{item.event}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Requirements & Guidelines</h2>
              <ul className="space-y-2">
                {event.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-2 sm:space-x-3">
                    <span className="w-2 h-2 bg-apollo-accent rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700 text-sm sm:text-base">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Attendees */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Attendees ({event.attendees})</h2>
                <button className="text-apollo-accent hover:text-apollo-primary text-sm font-medium">
                  View All
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {event.attendeesList.map(attendee => (
                  <div key={attendee.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-apollo-accent rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
                      {attendee.avatar}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{attendee.name}</div>
                      <div className="text-xs sm:text-sm text-gray-600 truncate">{attendee.department}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Event Info Card */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start space-x-3">
                  <CalendarDaysIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">Date</div>
                    <div className="text-gray-600 text-sm sm:text-base break-words">{new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">Time</div>
                    <div className="text-gray-600 text-sm sm:text-base">{event.time}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPinIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">Location</div>
                    <div className="text-gray-600 text-sm sm:text-base break-words">{event.location}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <UserGroupIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">Attendees</div>
                    <div className="text-gray-600 text-sm sm:text-base">{event.attendees} / {event.maxAttendees}</div>
                  </div>
                </div>

                <div className="pt-3 sm:pt-4 border-t">
                  <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getColorClasses(event.color)}`}>
                    {event.category}
                  </span>
                </div>
              </div>
            </div>

            {/* Organizer Info */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Event Organizer</h3>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start space-x-3">
                  <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <span className="text-gray-900 text-sm sm:text-base break-words">{event.organizer.name}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <span className="text-gray-600 text-sm sm:text-base">{event.organizer.contact}</span>
                </div>
                <div className="flex items-start space-x-3">
                  <EnvelopeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 mt-0.5" />
                  <span className="text-gray-600 text-sm sm:text-base break-all">{event.organizer.email}</span>
                </div>
              </div>
              <button className="w-full mt-3 sm:mt-4 px-3 sm:px-4 py-2 border border-apollo-accent text-apollo-accent rounded-lg hover:bg-apollo-accent hover:text-white transition-colors text-sm sm:text-base">
                Contact Organizer
              </button>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Quick Actions</h3>
              <div className="space-y-1 sm:space-y-2">
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base">
                  Add to Calendar
                </button>
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base">
                  Share Event
                </button>
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base">
                  Download Details
                </button>
                <button className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base">
                  Report Issue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default EventDetail;
